import { useEffect, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
} from "@mui/material";
import api from "../../../utils/api";

const today = new Date().toISOString().slice(0, 10);

export default function IncomeExpenseModal({ open, direction = "income", editingEntry = null, onClose }) {
    const isEdit = Boolean(editingEntry?.id);
    const resolvedDirection = isEdit ? editingEntry.direction : direction;
    const isExpense = resolvedDirection === "expense";

    const [form, setForm] = useState({
        amount: "",
        status: "processed",
        effective_date: today,
        description: "",
        expense_type_id: null,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [typesLoading, setTypesLoading] = useState(false);

    useEffect(() => {
        if (!open || !isExpense) return;
        let cancelled = false;
        const load = async () => {
            setTypesLoading(true);
            try {
                const res = await api.get("admin/expense-types/list");
                if (!cancelled) setExpenseTypes(res.data?.data || []);
            } catch (e) {
                console.error("Failed to load expense types", e);
            } finally {
                if (!cancelled) setTypesLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [open, isExpense]);

    useEffect(() => {
        if (!open) return;
        if (editingEntry) {
            const eff =
                typeof editingEntry.effective_date === "string"
                    ? editingEntry.effective_date.slice(0, 10)
                    : today;
            setForm({
                amount: editingEntry.amount != null ? String(editingEntry.amount) : "",
                type: editingEntry.type || "",
                status: editingEntry.status || "processed",
                effective_date: eff,
                description: editingEntry.description || "",
                expense_type_id: editingEntry.expense_type_id || editingEntry.expense_type?.id || null,
            });
        } else {
            setForm({
                amount: "",
                status: "processed",
                effective_date: today,
                description: "",
                expense_type_id: null,
            });
        }
        setError("");
    }, [open, direction, editingEntry, resolvedDirection]);

    const createEndpoint = isExpense ? "admin/income-expenses/expense" : "admin/income-expenses/income";

    const submit = async () => {
        setLoading(true);
        setError("");
        const body = {
            amount: Number(form.amount),
            status: form.status,
            effective_date: form.effective_date,
            description: form.description || null,
        };
        if (isExpense) {
            body.expense_type_id = form.expense_type_id || null;
        }
        if (isEdit) {
            body.type = form.type || null;
        }
        try {
            if (isEdit) {
                await api.put(`admin/income-expenses/${editingEntry.id}`, body);
            } else {
                await api.post(createEndpoint, body);
            }
            onClose(true);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    (typeof err.response?.data === "string" ? err.response.data : null) ||
                    "Could not save entry."
            );
        } finally {
            setLoading(false);
        }
    };

    const title = isEdit
        ? `Edit ${isExpense ? "expense" : "income"}`
        : isExpense
          ? "Add expense"
          : "Add income";

    const selectedType = expenseTypes.find((t) => Number(t.id) === Number(form.expense_type_id)) || null;

    return (
        <Dialog open={open} onClose={() => !loading && onClose(false)} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <OutlinedInput
                        type="number"
                        inputProps={{ min: 0.01, step: 0.01 }}
                        value={form.amount}
                        onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                        placeholder="Amount"
                        size="small"
                        startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                    />
                    {isExpense ? (
                        <Autocomplete
                            size="small"
                            options={expenseTypes}
                            value={selectedType}
                            loading={typesLoading}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                            onChange={(_, val) => setForm((prev) => ({ ...prev, expense_type_id: val?.id || null }))}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Expense type" label="Expense type" />
                            )}
                        />
                    ) : null}
                    {isEdit ? (
                        <OutlinedInput
                            value={form.type}
                            onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                            placeholder="Type"
                            size="small"
                        />
                    ) : null}
                    <OutlinedInput
                        type="date"
                        value={form.effective_date}
                        onChange={(e) => setForm((prev) => ({ ...prev, effective_date: e.target.value }))}
                        size="small"
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel id="income-expense-status-label">Status</InputLabel>
                        <Select
                            labelId="income-expense-status-label"
                            label="Status"
                            value={form.status}
                            onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                        >
                            <MenuItem value="processed">Processed</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                            <MenuItem value="canceled">Canceled</MenuItem>
                        </Select>
                    </FormControl>
                    <OutlinedInput
                        multiline
                        minRows={3}
                        value={form.description}
                        onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                        placeholder="Description"
                        size="small"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
                <Button variant="contained" disabled={loading || !form.amount || Number(form.amount) <= 0} onClick={submit}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
